import React, { ReactNode, useState } from 'react'
import { Form } from 'react-router-dom'
import InfoBox from '../components/InfoBox'
import PageLayout from '../components/PageLayout'

interface RatingPageProps {
  heading: string | ReactNode
  subheading?: string
  leftText: string
  rightText: string
  description?: string
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void
  buttonBgColor?: string
  headingSize?: string
  preselectedValue?: string
}

export default function RatingPage({
  heading,
  subheading,
  leftText,
  rightText,
  description,
  onSubmit,
  buttonBgColor = 'bg-transparent',
  headingSize = 'text-xl',
  preselectedValue,
}: RatingPageProps) {
  const [selectedRating, setSelectedRating] = useState<string | null>(
    preselectedValue || null,
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (onSubmit && selectedRating) {
      // Inject the selected rating into the form manually
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = 'rating'
      hiddenInput.value = selectedRating
      event.currentTarget.appendChild(hiddenInput)

      onSubmit(event)

      // Clean up the hidden input
      event.currentTarget.removeChild(hiddenInput)
    }
  }

  return (
    <PageLayout>
      <div className="flex grow flex-col">
        <div className="shrink-0 items-center justify-center text-center">
          <h1 className={`custom-heading mb-4 ${headingSize}`}>{heading}</h1>
          {subheading && <h2>{subheading}</h2>}
        </div>

        <div className="flex grow items-center justify-center pb-10">
          <Form
            method="post"
            className="mb-6 w-full max-w-md"
            onSubmit={handleSubmit}
          >
            {description && (
              <div className="mb-12">
                <InfoBox>{description}</InfoBox>
              </div>
            )}
            <div className="mb-4 flex w-full justify-between gap-1.5 sm:gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => {
                    setSelectedRating(rating.toString())
                    // Submit the form immediately after selecting a rating
                    setTimeout(() => {
                      document.querySelector('form')?.dispatchEvent(
                        new Event('submit', {
                          bubbles: true,
                          cancelable: true,
                        }),
                      )
                    }, 0)
                  }}
                  className={`border-custom-gray w-full cursor-pointer rounded-[6px] border bg-white ${buttonBgColor} px-2 py-4 text-center text-2xl sm:px-6 ${
                    selectedRating === rating.toString()
                      ? 'font-bold text-black'
                      : 'text-[#979797]'
                  } hover:opacity-80`}
                  data-testid={`button-option-${rating}`}
                >
                  {rating}
                </button>
              ))}
            </div>
            <div className="flex w-full justify-between text-sm">
              <span>{leftText}</span>
              <span>{rightText}</span>
            </div>
          </Form>
        </div>
      </div>
    </PageLayout>
  )
}
